import React, { useState, useEffect, useCallback, useRef } from "react"
import {
    Box,
    Button,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    CircularProgress,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"

const thStyle = {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase" as const,
    color: "#6b7280",
    letterSpacing: "0.05em",
}

const CustomersAdminPage: React.FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [companyId, setCompanyId] = useState("")
    const [customers, setCustomers] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [customerTypes, setCustomerTypes] = useState<any[]>([])
    const [searchQuery, setSearchQuery] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [filterType, setFilterType] = useState("")
    const [filterCustomerTypeId, setFilterCustomerTypeId] = useState("")
    const [filterStatus, setFilterStatus] = useState("active")
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(20)
    const [loading, setLoading] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get("/api/companies")
                if (cancelled) return
                const list = res.companies || []
                if (list.length > 0) setCompanyId(list[0].id)
            } catch (e: any) {
                console.error("Failed to load companies:", e)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!companyId) return
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get(
                    `/api/companies/${companyId}/customer-types`
                )
                if (cancelled) return
                setCustomerTypes(res.customerTypes || [])
            } catch (e: any) {
                console.error("Failed to load customer types:", e)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId])

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery)
            setPage(0)
        }, 300)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchQuery])

    const loadCustomers = useCallback(async () => {
        if (!companyId) return
        setLoading(true)
        try {
            const result = await adminService.getCustomers(companyId, {
                search: debouncedSearch || undefined,
                type: filterType || undefined,
                customer_type_id: filterCustomerTypeId || undefined,
                status: filterStatus || undefined,
                limit: pageSize,
                offset: page * pageSize,
            })
            setCustomers(result.customers)
            setTotal(result.total)
        } catch (error) {
            console.error("Failed to load customers:", error)
        } finally {
            setLoading(false)
        }
    }, [
        companyId,
        debouncedSearch,
        filterType,
        filterCustomerTypeId,
        filterStatus,
        page,
        pageSize,
    ])

    useEffect(() => {
        loadCustomers()
    }, [loadCustomers])

    const handleRowClick = (customerId: string) => {
        navigate(`/admin/customers/${customerId}`)
    }

    const getDisplayName = (customer: any) => {
        return customer.type === "business"
            ? customer.company_name || customer.name
            : customer.name || customer.company_name
    }

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography variant="h4">
                        {t("customersPages.list.title")}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/admin/customers/new")}
                    >
                        {t("customersPages.list.addCustomer")}
                    </Button>
                </Box>

                <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                        label={String(t("customersPages.list.filters.search"))}
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 250, flex: 1 }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={String(
                            t("customersPages.list.filters.searchPlaceholder")
                        )}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>
                            {String(t("customersPages.list.filters.legalForm"))}
                        </InputLabel>
                        <Select
                            value={filterType}
                            label={String(
                                t("customersPages.list.filters.legalForm")
                            )}
                            onChange={(e) => {
                                setFilterType(e.target.value)
                                setPage(0)
                            }}
                        >
                            <MenuItem value="">
                                {t("customersPages.list.filters.all")}
                            </MenuItem>
                            <MenuItem value="individual">
                                {t("customerForm.types.individual")}
                            </MenuItem>
                            <MenuItem value="business">
                                {t("customerForm.types.business")}
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>
                            {String(
                                t("customersPages.list.filters.customerType")
                            )}
                        </InputLabel>
                        <Select
                            value={filterCustomerTypeId}
                            label={String(
                                t("customersPages.list.filters.customerType")
                            )}
                            onChange={(e) => {
                                setFilterCustomerTypeId(e.target.value)
                                setPage(0)
                            }}
                        >
                            <MenuItem value="">
                                {t("customersPages.list.filters.all")}
                            </MenuItem>
                            {customerTypes.map((ct) => (
                                <MenuItem key={ct.id} value={ct.id}>
                                    {ct.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>
                            {String(t("customersPages.list.filters.status"))}
                        </InputLabel>
                        <Select
                            value={filterStatus}
                            label={String(
                                t("customersPages.list.filters.status")
                            )}
                            onChange={(e) => {
                                setFilterStatus(e.target.value)
                                setPage(0)
                            }}
                        >
                            <MenuItem value="active">
                                {t("customersPages.list.status.active")}
                            </MenuItem>
                            <MenuItem value="archived">
                                {t("customersPages.list.status.archived")}
                            </MenuItem>
                            <MenuItem value="all">
                                {t("customersPages.list.filters.all")}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={thStyle}>
                                    {t("customersPages.list.columns.name")}
                                </TableCell>
                                <TableCell sx={thStyle}>
                                    {t("customersPages.list.columns.type")}
                                </TableCell>
                                <TableCell sx={thStyle}>
                                    {t("customersPages.list.columns.email")}
                                </TableCell>
                                <TableCell sx={thStyle}>
                                    {t("customersPages.list.columns.phone")}
                                </TableCell>
                                <TableCell sx={thStyle}>
                                    {t("customersPages.list.columns.city")}
                                </TableCell>
                                <TableCell sx={thStyle}>
                                    {t(
                                        "customersPages.list.columns.customerType"
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        align="center"
                                        sx={{ py: 4 }}
                                    >
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : customers.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        align="center"
                                        sx={{ py: 4, color: "#9ca3af" }}
                                    >
                                        {t("customersPages.list.empty")}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                customers.map((customer) => (
                                    <TableRow
                                        key={customer.id}
                                        hover
                                        sx={{ cursor: "pointer" }}
                                        onClick={() =>
                                            handleRowClick(customer.id)
                                        }
                                    >
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {getDisplayName(customer)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t(
                                                    `customerForm.types.${customer.type}`
                                                )}
                                                size="small"
                                                color={
                                                    customer.type === "business"
                                                        ? "primary"
                                                        : "default"
                                                }
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {customer.email || t("common.dash")}
                                        </TableCell>
                                        <TableCell>
                                            {customer.phone || t("common.dash")}
                                        </TableCell>
                                        <TableCell>
                                            {customer.city || t("common.dash")}
                                        </TableCell>
                                        <TableCell>
                                            {customer.customer_type_name ||
                                                t("common.dash")}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(e) => {
                            setPageSize(parseInt(e.target.value, 10))
                            setPage(0)
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        labelRowsPerPage={t("common.rowsPerPage")}
                        labelDisplayedRows={({ from, to, count }) =>
                            String(
                                t("common.paginationDisplayedRows", {
                                    from,
                                    to,
                                    count: count !== -1 ? count : to,
                                })
                            )
                        }
                    />
                </TableContainer>
            </Container>
        </Box>
    )
}

export default CustomersAdminPage
